const React = require('react');
const { useEffect, useState } = React;
const { Card, Row, Col, Typography, message, Modal } = require('antd');
const { CarOutlined, DollarOutlined, BarChartOutlined, InfoCircleOutlined, EyeOutlined, MessageOutlined, EnvironmentOutlined, QuestionCircleOutlined, ArrowRightOutlined, LockOutlined } = require('@ant-design/icons');
const { useAuth } = require('../../contexts/AuthContext');

const { Title, Text } = Typography;

function currency(v) {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}

function startOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function ClientDashboard() {
  const { token, currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [metrics, setMetrics] = useState({
    vehicles: 0,
    tripsThisMonth: 0,
    activeCampaigns: 0,
    monthlyCost: 0,
    subscriptionStatus: null,
  });
  const [campaignPerf, setCampaignPerf] = useState({ totals: { impressions: 0, clicks: 0, conversions: 0, ctr: 0, cpa: 0, conversion_value: 0, amount_spent: 0, cpm: 0, cpc: 0, messages: 0, leads: 0, calls: 0 }, items: [] });

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const monthStart = startOfMonth(new Date());
        const since = isoDate(monthStart);
        const today = isoDate(new Date());

        const qsInvoices = new URLSearchParams({ limit: '100' }).toString();

        const [invRes, campRes, tripRes, subRes, perfRes] = await Promise.all([
          fetch(`/api/invoices/my?${qsInvoices}`, { headers }).catch(err => { console.warn('dashboard: invoices fetch failed', err); return { ok: false }; }),
          fetch('/api/vehicle-campaigns', { headers }).catch(err => { console.warn('dashboard: campaigns fetch failed', err); return { ok: false }; }),
          fetch('/api/trips', { headers }).catch(err => { console.warn('dashboard: trips fetch failed', err); return { ok: false }; }),
          fetch('/api/client/billing/subscription', { headers }).catch(err => { console.warn('dashboard: subscription fetch failed', err); return { ok: false }; }),
          fetch(`/api/analytics/campaign-performance?start_date=${encodeURIComponent(since)}&end_date=${encodeURIComponent(today)}`, { headers }).catch(err => { console.warn('dashboard: analytics fetch failed', err); return { ok: false }; }),
        ]);

        let monthlyCost = 0;
        if (invRes.ok) {
          try {
            const invData = await invRes.json();
            const invItems = Array.isArray(invData?.items) ? invData.items : (Array.isArray(invData) ? invData : []);
            monthlyCost = invItems
              .filter(i => i.issued_at && String(i.issued_at) >= since)
              .reduce((sum, i) => sum + Number(i.amount || 0), 0);
          } catch (e) { console.warn('dashboard: invoices parse error', e); }
        }

        let activeCampaigns = 0;
        if (campRes.ok) {
          try {
            const c = await campRes.json();
            const items = Array.isArray(c?.items) ? c.items : (Array.isArray(c) ? c : []);
            activeCampaigns = items.filter(x => (x.status || '').toLowerCase() === 'active').length;
          } catch (e) { console.warn('dashboard: campaigns parse error', e); }
        }

        // Assigned vehicles count
        let vehiclesCount = 0;
        try {
          const assignedRes = await fetch('/api/client-vehicle-assignments/my-vehicles', { headers });
          if (assignedRes.ok) {
            const assignedVehicles = await assignedRes.json();
            vehiclesCount = Array.isArray(assignedVehicles) ? assignedVehicles.length : 0;
          } else {
            console.warn('dashboard: vehicle assignments endpoint failed', assignedRes.status);
          }
        } catch (err) {
          console.warn('dashboard: vehicle assignments fetch error', err);
        }

        // Trips this month
        let tripsCount = 0;
        const to = new Date().toISOString();
        const sinceIso = new Date(monthStart).toISOString();
        try {
          const tripQs = new URLSearchParams({ from: sinceIso, to });
          const gtTrips = await fetch(`/api/integrations/geotab/trips/count?${tripQs.toString()}`, { headers });
          if (gtTrips.ok) {
            const tc = await gtTrips.json();
            tripsCount = Number(tc?.totalTrips || 0);
          } else {
            console.warn('dashboard: geotab trips count failed', gtTrips.status);
          }
        } catch (err) {
          console.warn('dashboard: geotab trips count error', err);
        }

        if (tripsCount === 0 && tripRes.ok) {
          try {
            const t = await tripRes.json();
            const items = Array.isArray(t?.items) ? t.items : (Array.isArray(t) ? t : []);
            const itemsThisMonth = items.filter(tr => {
              const d = tr.start_time || tr.date || tr.started_at;
              return d ? String(d) >= since : true;
            });
            tripsCount = itemsThisMonth.length;
          } catch (e) { console.warn('dashboard: local trips parse error', e); }
        }

        let subscriptionStatus = null;
        if (subRes.ok) {
          try {
            const s = await subRes.json();
            subscriptionStatus = s?.subscription_status || s?.status || null;
          } catch (e) { console.warn('dashboard: subscription parse error', e); }
        }

        // Campaign performance metrics (current month)
        let perfTotals = { impressions: 0, clicks: 0, conversions: 0, conversion_value: 0, amount_spent: 0, messages: 0, leads: 0, calls: 0, ctr: 0, cpa: 0, cpm: 0, cpc: 0 };
        let perfItems = [];
        if (perfRes.ok) {
          try {
            const p = await perfRes.json();
            perfItems = Array.isArray(p?.items) ? p.items : (Array.isArray(p) ? p : []);
            const totals = perfItems.reduce((acc, r) => {
              acc.impressions += Number(r.impressions || 0);
              acc.clicks += Number(r.clicks || 0);
              acc.conversions += Number(r.conversions || 0);
              acc.conversion_value += Number(r.conversion_value || 0);
              acc.amount_spent += Number(r.amount_spent || r.spend || 0);
              acc.messages += Number(r.messages || 0);
              acc.leads += Number(r.leads || 0);
              acc.calls += Number(r.calls || 0);
              return acc;
            }, { impressions: 0, clicks: 0, conversions: 0, conversion_value: 0, amount_spent: 0, messages: 0, leads: 0, calls: 0 });
            const ctr = totals.impressions > 0 ? +(totals.clicks / totals.impressions * 100).toFixed(2) : 0;
            const cpa = totals.conversions > 0 ? +(totals.conversion_value / totals.conversions).toFixed(2) : 0;
            const cpm = totals.impressions > 0 ? +(totals.amount_spent / totals.impressions * 1000).toFixed(2) : 0;
            const cpc = totals.clicks > 0 ? +(totals.amount_spent / totals.clicks).toFixed(2) : 0;
            perfTotals = { ...totals, ctr, cpa, cpm, cpc };
          } catch (e) { console.warn('dashboard: performance parse error', e); }
        }
        setCampaignPerf({ totals: perfTotals, items: perfItems });

        setMetrics({
          vehicles: vehiclesCount,
          tripsThisMonth: tripsCount,
          activeCampaigns,
          monthlyCost,
          subscriptionStatus,
        });

      } catch (e) {
        message.error(e.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    if (currentUser) run();
  }, [token, currentUser]);

  return React.createElement('div', { style: { padding: 24, maxWidth: 1400, margin: '0 auto' } },
    
    // Location Overview Section (White Card at Top)
    React.createElement('div', { style: { marginBottom: 32 } },
      React.createElement(Card, { 
        style: { 
          background: '#fff',
          border: '1px solid #e8e8e8',
          borderRadius: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        },
        bodyStyle: { padding: '32px' }
      },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', marginBottom: 24 } },
          React.createElement(EnvironmentOutlined, { style: { fontSize: 24, color: '#1890ff', marginRight: 12 } }),
          React.createElement('div', null,
            React.createElement('div', { style: { color: '#1f2937', fontSize: 18, fontWeight: 600 } }, 'Location Overview'),
            React.createElement('div', { style: { color: '#6b7280', fontSize: 13, marginTop: 2 } }, 'Performance metrics at a glance')
          )
        ),
        React.createElement(Row, { gutter: [32, 24] },
          React.createElement(Col, { xs: 12, sm: 6 },
            React.createElement('div', { style: { textAlign: 'center' } },
              React.createElement(CarOutlined, { style: { fontSize: 24, color: '#1890ff', marginBottom: 12 } }),
              React.createElement('div', { style: { fontSize: 32, fontWeight: 'bold', color: '#1f2937', marginBottom: 4 } }, loading ? '...' : metrics.vehicles),
              React.createElement('div', { style: { fontSize: 13, color: '#6b7280' } }, 'Vehicles')
            )
          ),
          React.createElement(Col, { xs: 12, sm: 6 },
            React.createElement('div', { style: { textAlign: 'center' } },
              React.createElement(InfoCircleOutlined, { style: { fontSize: 24, color: '#1890ff', marginBottom: 12 } }),
              React.createElement('div', { style: { fontSize: 32, fontWeight: 'bold', color: '#1f2937', marginBottom: 4 } }, loading ? '...' : metrics.activeCampaigns),
              React.createElement('div', { style: { fontSize: 13, color: '#6b7280' } }, 'Active Campaigns')
            )
          ),
          React.createElement(Col, { xs: 12, sm: 6 },
            React.createElement('div', { style: { textAlign: 'center' } },
              React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#1890ff', marginBottom: 12 } }),
              React.createElement('div', { style: { fontSize: 32, fontWeight: 'bold', color: '#1f2937', marginBottom: 4 } }, loading ? '...' : '88'),
              React.createElement('div', { style: { fontSize: 13, color: '#6b7280' } }, 'Impressions / Mile')
            )
          ),
          React.createElement(Col, { xs: 12, sm: 6 },
            React.createElement('div', { style: { textAlign: 'center' } },
              React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#1890ff', marginBottom: 12 } }),
              React.createElement('div', { style: { fontSize: 32, fontWeight: 'bold', color: '#1f2937', marginBottom: 4 } }, loading ? '...' : `${campaignPerf.totals.ctr.toFixed(1)}%`),
              React.createElement('div', { style: { fontSize: 13, color: '#6b7280' } }, 'Combined CTR')
            )
          )
        )
      )
    ),
    
    // Quick Actions Section
    React.createElement('div', { style: { marginBottom: 32 } },
      React.createElement(Title, { level: 5, style: { marginBottom: 16, fontWeight: 600 } }, 'Quick Actions'),
      React.createElement(Row, { gutter: [16, 16] },
        React.createElement(Col, { xs: 24, sm: 8 },
          React.createElement(Card, { 
            hoverable: true,
            style: { cursor: 'pointer', height: '100%' },
            bodyStyle: { padding: '24px' },
            onClick: () => window.location.href = '/client/campaigns/new'
          },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: '12px', background: '#f0f5ff', marginBottom: 16 } },
              React.createElement(EnvironmentOutlined, { style: { fontSize: 24, color: '#1890ff' } })
            ),
            React.createElement(Title, { level: 5, style: { margin: 0, marginBottom: 8 } }, 'Create Campaign'),
            React.createElement(Text, { type: 'secondary', style: { display: 'block', marginBottom: 16, fontSize: 13 } }, 'Launch a new marketing campaign for this location'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', color: '#1890ff', fontSize: 14, fontWeight: 500 } },
              React.createElement('span', null, 'Create Campaign'),
              React.createElement(ArrowRightOutlined, { style: { marginLeft: 8, fontSize: 12 } })
            )
          )
        ),
        React.createElement(Col, { xs: 24, sm: 8 },
          React.createElement(Card, { 
            hoverable: true,
            style: { cursor: 'pointer', height: '100%' },
            bodyStyle: { padding: '24px' },
            onClick: () => window.location.href = '/client/settings'
          },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: '12px', background: '#f0f5ff', marginBottom: 16 } },
              React.createElement(QuestionCircleOutlined, { style: { fontSize: 24, color: '#1890ff' } })
            ),
            React.createElement(Title, { level: 5, style: { margin: 0, marginBottom: 8 } }, 'Location Settings'),
            React.createElement(Text, { type: 'secondary', style: { display: 'block', marginBottom: 16, fontSize: 13 } }, 'Configure settings and preferences for this location'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', color: '#1890ff', fontSize: 14, fontWeight: 500 } },
              React.createElement('span', null, 'Settings'),
              React.createElement(ArrowRightOutlined, { style: { marginLeft: 8, fontSize: 12 } })
            )
          )
        ),
        React.createElement(Col, { xs: 24, sm: 8 },
          React.createElement(Card, { 
            hoverable: false,
            style: { cursor: 'not-allowed', height: '100%', background: '#f5f5f5', opacity: 0.7 },
            bodyStyle: { padding: '24px' },
            onClick: () => setUpgradeModalVisible(true)
          },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: '12px', background: '#d9d9d9', marginBottom: 16 } },
              React.createElement(LockOutlined, { style: { fontSize: 24, color: '#8c8c8c' } })
            ),
            React.createElement(Title, { level: 5, style: { margin: 0, marginBottom: 8, color: '#8c8c8c' } }, 'Manage Team'),
            React.createElement(Text, { type: 'secondary', style: { display: 'block', marginBottom: 16, fontSize: 13 } }, 'Upgrade to Enterprise to manage team members'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', color: '#8c8c8c', fontSize: 14, fontWeight: 500 } },
              React.createElement('span', null, 'Upgrade Required'),
              React.createElement(ArrowRightOutlined, { style: { marginLeft: 8, fontSize: 12 } })
            )
          )
        )
      )
    ),
    
    // META CAMPAIGNS Section
    React.createElement('div', { style: { marginBottom: 24 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', marginBottom: 12 } },
        React.createElement('div', { style: { width: 8, height: 8, borderRadius: '50%', background: '#3b5998', marginRight: 8 } }),
        React.createElement(Text, { strong: true, style: { fontSize: 12, color: '#8c8c8c', letterSpacing: '0.5px' } }, 'META CAMPAIGNS')
      ),
      React.createElement(Row, { gutter: [16, 16] },
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(InfoCircleOutlined, { style: { fontSize: 24, color: '#3b5998', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : Math.floor(metrics.activeCampaigns * 0.9)),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Number of Campaigns')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(DollarOutlined, { style: { fontSize: 24, color: '#3b5998', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : currency(campaignPerf.totals.amount_spent * 0.4)),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Total Spent'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 22.4%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#3b5998', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.ctr * 0.24).toFixed(1)}%`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'CTR'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 0.6%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(DollarOutlined, { style: { fontSize: 24, color: '#3b5998', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : currency(campaignPerf.totals.cpc * 0.53)),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'CPC'),
            React.createElement('div', { style: { fontSize: 11, color: '#f5222d', marginTop: 4 } }, '↓ 8.2%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(EyeOutlined, { style: { fontSize: 24, color: '#3b5998', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.impressions * 0.243 / 1000).toFixed(1)}K`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Impressions'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 16.3%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#3b5998', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : '86'),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Impressions / Mile'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 8.1%')
          )
        )
      )
    ),
    
    // GOOGLE ANALYTICS Section
    React.createElement('div', { style: { marginBottom: 32 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', marginBottom: 12 } },
        React.createElement('div', { style: { width: 8, height: 8, borderRadius: '50%', background: '#34a853', marginRight: 8 } }),
        React.createElement(Text, { strong: true, style: { fontSize: 12, color: '#8c8c8c', letterSpacing: '0.5px' } }, 'GOOGLE ANALYTICS')
      ),
      React.createElement(Row, { gutter: [16, 16] },
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(InfoCircleOutlined, { style: { fontSize: 24, color: '#34a853', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : Math.floor(metrics.activeCampaigns * 0.8)),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Active Campaigns')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(DollarOutlined, { style: { fontSize: 24, color: '#34a853', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : currency(campaignPerf.totals.amount_spent * 0.1456)),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Total Spent'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 19.2%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#34a853', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.ctr * 0.39).toFixed(1)}%`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'CTR'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 0.2%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(DollarOutlined, { style: { fontSize: 24, color: '#34a853', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : currency(campaignPerf.totals.cpc * 0.89)),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'CPC')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(EyeOutlined, { style: { fontSize: 24, color: '#34a853', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.impressions * 0.042 / 1000).toFixed(1)}K`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Impressions')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#34a853', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : '15'),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Impressions / Mile'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 7.3%')
          )
        )
      )
    ),
    
    // Analytics Overview Title
    React.createElement('div', { style: { marginBottom: 24 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center' } },
        React.createElement(BarChartOutlined, { style: { fontSize: 18, marginRight: 8 } }),
        React.createElement(Title, { level: 5, style: { margin: 0, fontWeight: 600 } }, 'Analytics Overview')
      )
    ),
    
    // SMS Analytics
    React.createElement('div', { style: { marginBottom: 24 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', marginBottom: 12 } },
        React.createElement('div', { style: { width: 8, height: 8, borderRadius: '50%', background: '#a855f7', marginRight: 8 } }),
        React.createElement(Text, { strong: true, style: { fontSize: 12, color: '#8c8c8c', letterSpacing: '0.5px' } }, 'SMS ANALYTICS')
      ),
      React.createElement(Row, { gutter: [16, 16] },
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(MessageOutlined, { style: { fontSize: 24, color: '#a855f7', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : Math.floor(metrics.activeCampaigns * 0.67)),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Active Campaigns')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(MessageOutlined, { style: { fontSize: 24, color: '#a855f7', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.messages * 7.1 / 1000).toFixed(1)}K`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Number Delivered'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 19.5%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#a855f7', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.ctr * 0.42).toFixed(1)}%`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'CTR'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 2.1%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#a855f7', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.clicks * 2.8 / 1000).toFixed(1)}K`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Total Miles'),
            React.createElement('div', { style: { fontSize: 11, color: '#f5222d', marginTop: 4 } }, '↓ 3.4%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(EyeOutlined, { style: { fontSize: 24, color: '#a855f7', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : '2.5'),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Impressions / Mile'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 9.2%')
          )
        )
      )
    ),
    
    // EMAIL ANALYTICS
    React.createElement('div', { style: { marginBottom: 32 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', marginBottom: 12 } },
        React.createElement('div', { style: { width: 8, height: 8, borderRadius: '50%', background: '#3b82f6', marginRight: 8 } }),
        React.createElement(Text, { strong: true, style: { fontSize: 12, color: '#8c8c8c', letterSpacing: '0.5px' } }, 'EMAIL ANALYTICS')
      ),
      React.createElement(Row, { gutter: [16, 16] },
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(MessageOutlined, { style: { fontSize: 24, color: '#3b82f6', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : Math.floor(metrics.activeCampaigns * 0.67)),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Active Campaigns')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(EyeOutlined, { style: { fontSize: 24, color: '#3b82f6', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.impressions * 13.3 / 1000).toFixed(1)}K`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Emails Delivered'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 18.2%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#3b82f6', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.ctr * 2.53).toFixed(1)}%`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Open Rate'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 3.2%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#3b82f6', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.ctr * 0.58).toFixed(1)}%`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'CTR'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 1.8%')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(BarChartOutlined, { style: { fontSize: 24, color: '#3b82f6', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : `${(campaignPerf.totals.clicks * 2.8 / 1000).toFixed(1)}K`),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Total Miles')
          )
        ),
        React.createElement(Col, { xs: 12, sm: 8, md: 4 },
          React.createElement(Card, { bodyStyle: { padding: '20px', textAlign: 'center', minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center' } },
            React.createElement(EyeOutlined, { style: { fontSize: 24, color: '#3b82f6', marginBottom: 8 } }),
            React.createElement('div', { style: { fontSize: 28, fontWeight: 'bold', marginBottom: 4 } }, loading ? '...' : '4.7'),
            React.createElement('div', { style: { fontSize: 12, color: '#8c8c8c' } }, 'Impressions / Mile'),
            React.createElement('div', { style: { fontSize: 11, color: '#52c41a', marginTop: 4 } }, '↑ 6.7%')
          )
        )
      )
    ),

    // Upgrade Modal
    React.createElement(Modal, {
      title: 'Upgrade to Enterprise',
      open: upgradeModalVisible,
      onCancel: () => setUpgradeModalVisible(false),
      footer: [
        React.createElement('button', {
          key: 'close',
          onClick: () => setUpgradeModalVisible(false),
          style: {
            padding: '8px 16px',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            background: '#fff',
            cursor: 'pointer'
          }
        }, 'Close'),
        React.createElement('button', {
          key: 'upgrade',
          onClick: () => window.location.href = '/client/billing',
          style: {
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            background: '#1890ff',
            color: '#fff',
            cursor: 'pointer',
            marginLeft: '8px'
          }
        }, 'View Plans')
      ]
    },
      React.createElement('div', { style: { textAlign: 'center', padding: '20px' } },
        React.createElement(LockOutlined, { style: { fontSize: 64, color: '#1890ff', marginBottom: 16 } }),
        React.createElement(Title, { level: 4 }, 'Team Management Feature'),
        React.createElement(Text, { type: 'secondary', style: { display: 'block', marginBottom: 16 } },
          'Manage team members and collaborate with your organization by upgrading to our Enterprise plan.'
        ),
        React.createElement('ul', { style: { textAlign: 'left', marginTop: 20, marginBottom: 20 } },
          React.createElement('li', null, 'Add and manage unlimited team members'),
          React.createElement('li', null, 'Assign roles and permissions'),
          React.createElement('li', null, 'Team collaboration tools'),
          React.createElement('li', null, 'Advanced reporting and analytics'),
          React.createElement('li', null, 'Priority support')
        )
      )
    )
  );
}

export default ClientDashboard;
